import { convexQuery } from "@convex-dev/react-query";
import { useNumberFormat } from "@react-input/number-format";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import type {
  Control,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
} from "react-hook-form";

import { type LocationListItem, organizationRolesGetRoles } from "~/api-refs";
import { LogoUploader } from "~/components/locations/logo-uploader";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "~/components/ui/input-group";
import { MultiCombobox } from "~/components/ui/multi-combobox";
import { PasswordInput } from "~/components/ui/password-input";

type EmployeeFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = {
  control: Control<TFieldValues>;
  name: TName;
};

/** Narrow an untyped form field value to the string list the combobox needs. */
function toStringList(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const entries: unknown[] = value;
  return entries.filter((entry): entry is string => typeof entry === "string");
}

export const EmployeeLogoField = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  setUploading,
}: EmployeeFieldProps<TFieldValues, TName> & {
  setUploading: (uploading: boolean) => void;
}) => {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="items-center">
          <FormControl className="items-center">
            <LogoUploader
              onValueChange={(url) => {
                field.onChange(url);
              }}
              photoName="photo"
              setUploading={setUploading}
              value={field.value ?? null}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export const EmployeeNameField = <TFieldValues extends FieldValues>({
  control,
  firstNameField,
  lastNameField,
}: {
  control: Control<TFieldValues>;
  firstNameField: FieldPath<TFieldValues>;
  lastNameField: FieldPath<TFieldValues>;
}) => {
  return (
    <div className="grid-cols-2 flex-col items-center justify-center gap-4 space-y-6">
      <EmployeeFirstNameField control={control} name={firstNameField} />
      <EmployeeLastNameField control={control} name={lastNameField} />
    </div>
  );
};

export const EmployeeFirstNameField = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
}: EmployeeFieldProps<TFieldValues, TName>) => {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>First Name</FormLabel>
          <FormControl>
            <Input {...field} maxLength={50} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export const EmployeeLastNameField = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
}: EmployeeFieldProps<TFieldValues, TName>) => {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>Last name</FormLabel>
          <FormControl>
            <Input {...field} maxLength={50} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export const EmployeePhoneField = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
}: EmployeeFieldProps<TFieldValues, TName>) => {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>Phone number (optional)</FormLabel>
          <FormControl>
            <PhoneInput field={field} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

const PhoneInput = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  field,
}: {
  field: ControllerRenderProps<TFieldValues, TName>;
}) => {
  return (
    <Input
      {...field}
      inputMode="tel"
      maxLength={10}
      placeholder="5551234567"
      value={typeof field.value === "string" ? field.value : ""}
    />
  );
};

export const EmployeeEmailField = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  required = false,
}: EmployeeFieldProps<TFieldValues, TName> & {
  required?: boolean;
}) => {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>
            {required ? "Email address" : "Email address (optional)"}
          </FormLabel>
          <FormControl>
            <Input
              {...field}
              inputMode="email"
              maxLength={100}
              value={typeof field.value === "string" ? field.value : ""}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
      rules={
        required
          ? { required: "Email is required for Portal access" }
          : undefined
      }
    />
  );
};

export const EmployeePinField = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
}: EmployeeFieldProps<TFieldValues, TName>) => {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex items-center">
          <FormLabel className="body text-muted-foreground">
            Choose a PIN for this employee to log into POS
          </FormLabel>
          <FormLabel className="label">4 digit PIN</FormLabel>
          <FormControl>
            <PasswordInput
              {...field}
              autoComplete="off"
              data-1p-ignore
              data-bwignore
              data-form-type="other"
              data-lpignore="true"
              maxLength={4}
              minLength={4}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export const EmployeeRolesField = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  createNewOption,
}: EmployeeFieldProps<TFieldValues, TName> & {
  createNewOption?: (name: string) => void;
}) => {
  const { data: roles } = useQuery(convexQuery(organizationRolesGetRoles, {}));

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const roleValues = toStringList(field.value);

        return (
          <FormItem>
            <FormLabel>Roles</FormLabel>
            <FormControl>
              <MultiCombobox
                groups={["default"]}
                createNewOption={
                  createNewOption && {
                    label: "Create new role",
                    onClick: (roleName) => {
                      createNewOption(roleName);
                    },
                  }
                }
                items={{
                  default: roles?.length
                    ? roles.map((r) => ({
                        value: r._id,
                        label: r.name,
                      }))
                    : [],
                }}
                onValueChange={(val) => field.onChange(val.default)}
                values={{
                  default: roleValues,
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};

export const EmployeeLocationField = <TFieldValues extends FieldValues>({
  control,
  name,
  onAllLocationsSelectedChange,
  locations,
}: {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  /** Called with whether every known location is currently selected. */
  onAllLocationsSelectedChange: (allSelected: boolean) => void;
  locations: LocationListItem[] | undefined;
}) => {
  return (
    <FormField
      control={control}
      disabled={!locations}
      name={name}
      render={({ field }) => {
        const locationValues = toStringList(field.value);

        return (
          <FormItem>
            <FormLabel>Locations</FormLabel>
            <FormControl>
              <MultiCombobox
                groups={["default"]}
                allOption={{
                  dropdownLabel: "All and future locations",
                  selectedLabel: "All and future locations",
                }}
                groupLabels={{
                  default: "Your locations",
                }}
                items={{
                  default: locations
                    ? locations.map((r) => ({
                        value: r.id,
                        label: r.name,
                        imageUrl: r.logoUrl ?? "/images/avatar-placeholder.svg",
                      }))
                    : [],
                }}
                onValueChange={(val) => {
                  field.onChange(val.default);
                  onAllLocationsSelectedChange(
                    val.default.length === locations?.length
                  );
                }}
                values={{
                  default: locationValues,
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};

export const EmployeeHourlyRateField = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
}: EmployeeFieldProps<TFieldValues, TName>) => {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>Hourly rate (optional)</FormLabel>
          <FormControl>
            <HourlyRateFieldInternal field={field} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

const HourlyRateFieldInternal = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  field,
}: {
  field: ControllerRenderProps<TFieldValues, TName>;
}) => {
  const hourlyRateInputRef = useNumberFormat({
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    maximumIntegerDigits: 3,
    locales: ["en-US"],
  });

  useEffect(() => {
    field.ref(hourlyRateInputRef.current);
  }, [hourlyRateInputRef, field]);

  return (
    <InputGroup className="h-9 rounded-full">
      <InputGroupAddon align="inline-start">
        <InputGroupText>$</InputGroupText>
      </InputGroupAddon>
      <InputGroupInput
        {...field}
        className="body-sm placeholder:body-sm px-3 py-2"
        inputMode="decimal"
        placeholder="0.00"
        ref={hourlyRateInputRef}
        value={field.value ?? ""}
      />
    </InputGroup>
  );
};
